import type { Money } from "@graphql/generated/types";
import type { SaleBadge } from "@lib/wrappers/algolia/types";

export enum EventTypes {
    userData = "user_data",
    cartContents = "cart_contents",
    selectItem = "select_item",
    viewItem = "view_item",
    loadItemsList = "load_item_list",
    viewItemsList = "view_item_list",
    addToCart = "add_to_cart",
    addToWishlist = "add_to_wishlist",
    removeFromWishlist = "remove_from_wishlist",
    removeFromCart = "remove_from_cart",
    viewCart = "view_cart",
    beginCheckout = "begin_checkout",
    addShippingInfo = "add_shipping_info",
    addPaymentInfo = "add_payment_info",
    purchase = "purchase",
    newsletterSubscribe = "newsletter_subscribe",
    listFilterApplied = "list_filter_applied",
    listSortingApplied = "list_sorting_applied",
    purchaseStatus = "purchase_status",
    accountCreate = "account_create",
    clubSignup = "club_signup",
    viewPromotion = "view_promotion",
    selectPromotion = "select_promotion",
    pageView = "page_view",
    viewItemsFromCartRecommendations = "view_items_from_cart_recommendations",
    selectItemFromCartRecommendations = "select_item_from_cart_recommendations",
    addToCartFromCartRecommendations = "add_to_cart_from_cart_recommendations",
    mini_bag_close = "mini_bag_close",
    mini_bag_view = "mini_bag_view",
    mini_bag_scroll = "mini_bag_scroll",
    mini_bag_click = "mini_bag_click",
    checkoutProgress = "checkout_progress",
    checkoutValidationError = "checkout_validation_error",
    tryAndBuyInitiated = "try_and_buy_initiated",
    tryAndBuyCompleted = "try_and_buy_completed",
    tryAndBuyDropOff = "try_and_buy_drop_off",
    tryAndBuySignUpInitiated = "try_and_buy_signup_initiated",
    viewStockNotification = "view_stock_notification",
    sendStockNotification = "send_stock_notification",
    search = "search",
    sizeWizard = "size_wizard",
    signupPopupViewed = "signup_popup_viewed",
    signupPopupStarted = "signup_popup_started",
    signupPopupCtaClicked = "signup_popup_cta_clicked",
    signupPopupSubmitted = "signup_popup_submitted",
    signupPopupSuccess = "signup_popup_success",
    signupPopupError = "signup_popup_error",
    signupPopupClosed = "signup_popup_closed",
    signupPopupClosedButton = "signup_popup_closed_button",
    invalidEmail = "invalid_email",
    invalidPhone = "invalid_phone",
    missingConsent = "missing_consent",
}

export type ClubSignupMethod =
    | "email"
    | "google"
    | "facebook"
    | "apple"
    | "naver"
    | "kakao"
    | "line";

export type ClubSignupErrorType = "validation_error" | "technical_error";

export type ClubSignupEvent =
    | {
          stepName: "signup_opened";
          signupMethod: null;
      }
    | {
          stepName: "signup_method_selected";
          signupMethod: ClubSignupMethod;
      }
    | {
          stepName: "account_details_submitted";
          signupMethod: ClubSignupMethod;
          marketingConsent: boolean;
      }
    | {
          stepName: "profile_details_submitted";
          signupMethod: ClubSignupMethod;
          genderProvided: boolean;
          birthDateProvided: boolean;
      }
    | {
          stepName: "profile_details_skipped" | "signup_completed";
          signupMethod: ClubSignupMethod;
      }
    | {
          stepName: "signup_error";
          stepNumber: 1 | 2 | 3 | 4;
          signupMethod: ClubSignupMethod;
          errorType: ClubSignupErrorType;
          errorField?: string;
      };

export const SearchAnalyticsType = {
    normal: "normal search",
    instant: "instant search",
} as const;

export type SearchAnalyticsType = (typeof SearchAnalyticsType)[keyof typeof SearchAnalyticsType];

export enum CheckoutProgressSubtypes {
    beginCheckout = "begin_checkout",
    deliveryMethodSelected = "delivery_method_selected",
    addShippingInfoFormStarted = "add_shipping_info_form_started",
    goToPaymentSelections = "go_to_payment_selections",
    paymentMethodSelected = "payment_method_selected",
    goToPaymentGateway = "go_to_payment_gateway",
    purchase = "purchase",
}

export type CheckoutType = "normal" | "express";

export type AddToCartSource = "sticky";

/** Opt-in fields for the checkout funnel events; callers that omit them keep the old payload. */
export type CheckoutEventOptions = {
    checkoutType?: CheckoutType;
    currency?: string;
};

export type PurchaseEventOverrides = {
    currency?: string;
    coupon?: string;
    affiliation?: string;
    shipping?: number;
    shipping_tier?: string;
    payment_type?: string;
    purchase_store_name?: string;
    visitor_phone?: string;
    visitor_postal_code?: string;
    visitor_region?: string;
};

export type PurchaseEventOptions = {
    /** Defaults to normal; only express checkout passes express. */
    checkoutType?: CheckoutType;
    overrides?: PurchaseEventOverrides;
};

export type CheckoutValidationError = {
    target: string;
    message: string;
};

export enum AlgoliaEventMethods {
    init = "init",
    clickedFilters = "clickedFilters",
    viewedFilters = "viewedFilters",
    clickedObjectIDsAfterSearch = "clickedObjectIDsAfterSearch",
    clickedObjectIDs = "clickedObjectIDs",
    purchasedObjectIDsAfterSearch = "purchasedObjectIDsAfterSearch",
    purchasedObjectIDs = "purchasedObjectIDs",
    convertedObjectIDsAfterSearch = "convertedObjectIDsAfterSearch",
    convertedObjectIDs = "convertedObjectIDs",
    addedToCartObjectIDsAfterSearch = "addedToCartObjectIDsAfterSearch",
    addedToCartObjectIDs = "addedToCartObjectIDs",
    convertedFilters = "convertedFilters",
    viewedObjectIDs = "viewedObjectIDs",
}

export type ListData = {
    listName?: string;
    listId?: string;
    position?: number;
    lastAppliedFilter?: any;
    recommendationCartItemId?: string;
    recommendationCartItemName?: string;
};

export enum AnalyticsListTypes {
    plp = "product_listing_page",
    search = "search",
    recommendations = "recommendations",
    recentlyViewed = "recently_viewed",
    upsellInCart = "upsell_in_cart",
    custom = "custom",
}

export type AnalyticDefaultItem = {
    item_id: string | number; // ObjectID
    item_variant: string; //sku
    item_name: string;
    promotion_name?: string;
    promotion_id?: string;
    item_list_name?: string;
    item_list_id?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_brand: string;
    currency: string;
    price: number;
    quantity: number;
    index: number;
    discount?: number;
    coupon: string;
    list_sorting?: string;
    list_filter_category?: string;
    list_filter_name?: string;
    list_search_key?: string;
    barcode: string;
    recommendation_cart_item_id?: string;
    recommendation_cart_item_name?: string;
};

export type StockAvailability = {
    stock_count_online: Record<string, string>;
    in_stock_size: string[];
    out_of_stock_size: string[];
};

export type AnalyticsExtendedItem = AnalyticDefaultItem & {
    colour?: string;
    variant_price?: number;
    full_price?: number;
    default_variant?: string;
    size?: string;
    size_imperial?: string;
    size_type?: string;
    barcode?: string;
    stock_count_online?: number | StockAvailability["stock_count_online"];
    in_stock_size?: string[];
    out_of_stock_size?: string[];
    stock_count_offline?: number;
    on_sale?: string;
    label_name?: string;
    label_source?: string;
    source?: string;
    discounted_line_total?: number;
};

export interface AlgoliaProductColor {
    colorCode: string;
    image: string;
    imageLabel?: string;
    priceWithoutDiscount: Money;
    objectID: string;
    altTagPattern?: string;
    priceWithDiscount: Money;
    hasDiscount: boolean;
    eaBadge?: SaleBadge;
    hasStock?: boolean;
    discountPercentage: number;
    lowestPrice?: Money;
    discountPercentageLowestPrice?: number;
    dominantColor?: string;
    backgroundColor?: string;
    customBadges?: string[];
}

export type AlgoliaItem = {
    id: unknown;
    name: string;
    dominantColor: string;
    gender: string;
    factsAndBenefits: string[];
    price: number;
    priceRange: string;
    collectionClass: string;
    collectionSegment: string;
    hasStock: boolean;
    collectionType: string;
    division: string;
    sizes: string[];
    variants: { size: string; availableQuantity: number; sku: string }[];
    listPrice: number;
    materialGroup: string;
    productColors: AlgoliaProductColor[];
    objectID: string;
    queryID: string;
    index: number;
    productName: string;
    articleNumber: string;
    colorCode: string;
    categories: string[];
    image: string;
    hasDiscount: boolean;
    eaBadge: SaleBadge | undefined;
    listPriceRaw: Money;
    priceRaw: Money;
    discountPercentage: number;
    descriptiveHeading: string;
    badges?: string[];
    // UI compatibility fields
    priceWithDiscount: Money;
    priceWithoutDiscount: Money;
    lowestPrice?: Money;
    discountPercentageLowestPrice?: number;
    colorLabel: string;
};
